import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  Skeleton,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import { EmptyState } from 'components/shared/EmptyState';
import { SPACING } from 'components/shared/constants';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { AttentionFeedTile } from './AttentionFeedTile';
import type { AttentionCamera, UseAttentionFeedReturn } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AttentionFeedProps {
  /**
   * Attention feed data from the useAttentionFeed hook.
   * When undefined, the component renders in loading state.
   */
  feed?: UseAttentionFeedReturn;
  /** Whether the feed data is loading */
  loading?: boolean;
  /** Callback when a camera tile is clicked (opens CameraDetail) */
  onCameraClick?: (camera: AttentionCamera) => void;
  /** Additional sx styles */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CAMERA_COUNT_OPTIONS = [4, 6, 9, 12] as const;
const SKELETON_COUNT = 6;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Attention Feed panel displaying high-severity camera streams in a CSS Grid.
 *
 * Subscribes to the useAttentionFeed hook for camera data and renders each
 * camera as an AttentionFeedTile with live video, severity badge, and
 * dismiss controls.
 *
 * Layout:
 *  - Header: WarningAmberIcon + "Attention Feed" title + camera count Select
 *  - Grid: repeat(min(cameras.length, 3), 1fr) with SPACING.sm gap
 *  - EmptyState when no cameras meet the severity threshold
 *  - Skeleton grid while loading
 */
export function AttentionFeed({ feed, loading = false, onCameraClick, sx }: AttentionFeedProps) {
  const cameras = feed?.cameras ?? [];
  const maxCameras = feed?.maxCameras ?? CAMERA_COUNT_OPTIONS[1];

  // -- Context: cross-section linking -----------------------------------------
  const { highlightedCameraId } = useCANvisionContext();

  // -- Handlers ---------------------------------------------------------------

  const handleMaxCamerasChange = (event: SelectChangeEvent<number>) => {
    feed?.setMaxCameras(Number(event.target.value));
  };

  const handleDismiss = (cameraId: number) => {
    feed?.dismissCamera(cameraId);
  };

  // -- Render -----------------------------------------------------------------

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" sx={{ fontSize: 22 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Attention Feed
          </Typography>
          {cameras.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              ({cameras.length})
            </Typography>
          )}
        </Box>

        <Select
          value={maxCameras}
          onChange={handleMaxCamerasChange}
          size="small"
          variant="outlined"
          sx={{ minWidth: 72, '& .MuiSelect-select': { py: 0.5, fontSize: '0.85rem' } }}
        >
          {CAMERA_COUNT_OPTIONS.map((count) => (
            <MenuItem key={count} value={count}>
              {count}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {loading ? (
          /* Loading: Skeleton grid */
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: SPACING.sm,
            }}
          >
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                sx={{ paddingTop: '56.25%', width: '100%', borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : cameras.length === 0 ? (
          /* Empty state */
          <EmptyState
            icon={<VideocamOffIcon />}
            title="No high-severity events detected"
            message="Cameras will appear here when critical or high-severity events are triggered."
            size="medium"
          />
        ) : (
          /* Camera grid — always 2 per row */
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
              gap: SPACING.sm,
            }}
          >
            {cameras.map((camera) => (
              <AttentionFeedTile
                key={camera.cameraId}
                camera={camera}
                onDismiss={handleDismiss}
                onClick={onCameraClick}
                isHighlighted={highlightedCameraId === camera.cameraId}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default AttentionFeed;
